import { useMemo, useRef } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import { ReferencePanel } from "./ReferencePanel";
import {
  unknownOperands,
  type CalculationFormValues,
  type OperandGroups,
} from "./types";
import "./style.css";

export interface CalculationFormProps {
  mode: "create" | "edit";
  values: CalculationFormValues;
  onChange: (values: CalculationFormValues) => void;
  slug: string;
  operands: OperandGroups;
}

export const CalculationForm = ({
  mode,
  values,
  onChange,
  slug,
  operands,
}: CalculationFormProps): JSX.Element => {
  const exprRef = useRef<HTMLTextAreaElement>(null);

  const set = <K extends keyof CalculationFormValues>(
    k: K,
    v: CalculationFormValues[K]
  ) => onChange({ ...values, [k]: v });

  const operandSlugs = useMemo(
    () => [
      ...operands.questions.map((o) => o.slug),
      ...operands.lookups.map((o) => o.slug),
      ...operands.calculations.map((o) => o.slug),
    ],
    [operands]
  );
  const unknown = unknownOperands(values.expression, operandSlugs);

  const insertToken = (token: string) => {
    const el = exprRef.current;
    const expr = values.expression;
    const start =
      el && el.selectionStart != null ? el.selectionStart : expr.length;
    const end = el && el.selectionEnd != null ? el.selectionEnd : expr.length;
    const next = expr.slice(0, start) + token + expr.slice(end);
    set("expression", next);
    requestAnimationFrame(() => {
      if (el) {
        const caret = start + token.length;
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  };

  return (
    <div className="calculations-tab-form">
      <div className="row">
        <div className="col-sm-8">
          <Form.Group className="mb-3">
            <Form.Label htmlFor="calc-name">Name of the result</Form.Label>
            <TextField
              label={null}
              id="calc-name"
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label htmlFor="calc-expression">
              Calculation to be performed
            </Form.Label>
            <Form.Control
              as="textarea"
              id="calc-expression"
              aria-label="Calculation to be performed"
              ref={exprRef}
              rows={3}
              placeholder="floors * area_per_floor"
              value={values.expression}
              onChange={(e) => set("expression", e.target.value)}
            />
          </Form.Group>
          {unknown.length > 0 && (
            <small className="text-warning d-block">
              Unknown operand(s): {unknown.join(", ")}
            </small>
          )}
          {mode === "edit" && (
            <Form.Group className="mt-3">
              <Form.Label htmlFor="calc-slug">Slug</Form.Label>
              <TextField
                label={null}
                id="calc-slug"
                value={slug}
                onChange={() => undefined}
                readOnly
                disabled
              />
              <small className="text-muted d-block">
                Used as an operand in other calculations.
              </small>
            </Form.Group>
          )}
        </div>
        <div className="col-sm-4">
          <ReferencePanel operands={operands} onInsert={insertToken} />
        </div>
      </div>
    </div>
  );
};

export default CalculationForm;
